import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Phone, ShieldCheck, ArrowRight, Loader2, RotateCcw, LogIn, UserPlus } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { useAuth } from "@/context/AuthContext";

const COPY = {
  create: { title: "Create your One Talanta ID", sub: "Your single identity across all six pillars.", cta: "Send code" },
  signin: { title: "Welcome back", sub: "Sign in with the phone number on your One Talanta ID.", cta: "Send sign-in code" },
};

const ModeToggle = ({ mode, setMode }) => (
  <div className="grid grid-cols-2 rounded-xl bg-talanta-cream p-1 text-sm font-semibold" role="tablist" data-testid="auth-mode-toggle">
    {[
      { id: "create", label: "Create ID", icon: UserPlus },
      { id: "signin", label: "Sign in", icon: LogIn },
    ].map(({ id, label, icon: Icon }) => (
      <button
        key={id}
        role="tab"
        aria-selected={mode === id}
        data-testid={`auth-mode-${id}`}
        onClick={() => setMode(id)}
        className={`flex items-center justify-center gap-2 rounded-lg py-2 transition-colors ${
          mode === id ? "bg-talanta-green text-white shadow" : "text-talanta-charcoal/60 hover:text-talanta-green"
        }`}
      >
        <Icon className="h-4 w-4" /> {label}
      </button>
    ))}
  </div>
);

export const OneTalantaIDAuthModal = ({ open, onOpenChange, prefillPhone, initialMode = "create" }) => {
  const { requestOtp, verifyOtp } = useAuth();
  const [mode, setMode] = useState(initialMode);
  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [consent, setConsent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const timerRef = useRef(null);
  const isSignin = mode === "signin";

  useEffect(() => {
    if (open) {
      setStep("phone");
      setCode("");
      setPhone(prefillPhone || "");
      setMode(initialMode);
    }
  }, [open, prefillPhone, initialMode]);

  useEffect(() => {
    if (countdown <= 0) return;
    timerRef.current = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timerRef.current);
  }, [countdown]);

  const send = async () => {
    if (!phone.trim()) return toast.error("Enter your phone number");
    if (!isSignin && !consent) return toast.error("Please accept the Privacy Policy and Terms to continue");
    setBusy(true);
    try {
      const res = await requestOtp(phone);
      setStep("otp");
      setCountdown(30);
      if (res.dev_code) toast.success(`Code sent. Dev code: ${res.dev_code}`, { duration: 8000 });
      else toast.success("Verification code sent via SMS");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send code");
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    if (code.length !== 6) return toast.error("Enter the 6-digit code");
    setBusy(true);
    try {
      const user = await verifyOtp(phone, code, isSignin ? undefined : name, !isSignin && consent);
      toast.success(`${isSignin ? "Welcome back" : "Welcome to One Talanta ID"} · ${user.talanta_id}`);
      onOpenChange(false);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Verification failed";
      if (isSignin && e?.response?.status === 400 && /Privacy Policy/i.test(detail)) {
        toast.error("No One Talanta ID exists for this number yet — create one below.", { duration: 6000 });
        setMode("create");
        setStep("phone");
        setCode("");
      } else {
        toast.error(detail);
        setCode("");
      }
    } finally {
      setBusy(false);
    }
  };

  const copy = COPY[mode];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="auth-modal" className="sm:max-w-md p-0 overflow-hidden border-0 bg-white max-h-[92vh] overflow-y-auto">
        <DialogTitle className="sr-only">One Talanta ID {isSignin ? "sign in" : "registration"}</DialogTitle>
        <DialogDescription className="sr-only">Sign in or create your One Talanta ID using SMS verification.</DialogDescription>
        <div className="kenya-stripe h-1.5" />
        <div className="bg-talanta-green px-6 pt-6 pb-8 text-white">
          <div className="flex items-center gap-2 text-talanta-gold text-xs font-semibold uppercase tracking-widest">
            <ShieldCheck className="h-4 w-4" /> One Talanta ID
          </div>
          <h2 className="mt-2 font-display font-bold text-2xl" data-testid="auth-modal-title">
            {step === "phone" ? copy.title : "Verify your number"}
          </h2>
          <p className="mt-1 text-sm text-white/70">
            {step === "phone" ? copy.sub : `We sent a 6-digit code to ${phone}`}
          </p>
        </div>

        <div className="px-6 py-6">
          {step === "phone" ? (
            <div className="space-y-4">
              <ModeToggle mode={mode} setMode={setMode} />
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-talanta-charcoal/50">Phone number</label>
                <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-talanta-green/15 bg-talanta-cream px-3 focus-within:ring-2 focus-within:ring-talanta-green">
                  <Phone className="h-4 w-4 text-talanta-green/50" />
                  <input
                    data-testid="phone-input"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    inputMode="tel"
                    autoFocus
                    placeholder="07XX XXX XXX or +2547XX XXX XXX"
                    className="flex-1 bg-transparent py-3 outline-none text-talanta-charcoal font-medium"
                    onKeyDown={(e) => e.key === "Enter" && send()}
                  />
                </div>
              </div>
              {!isSignin && (
                <>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-talanta-charcoal/50">Name (optional)</label>
                    <input
                      data-testid="name-input"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="How should we call you?"
                      className="mt-1.5 w-full rounded-xl border border-talanta-green/15 bg-talanta-cream px-4 py-3 outline-none focus:ring-2 focus:ring-talanta-green text-talanta-charcoal font-medium"
                    />
                  </div>
                  <label className="flex items-start gap-3 rounded-xl border border-talanta-green/10 bg-talanta-cream/60 px-3 py-3 cursor-pointer">
                    <input
                      data-testid="privacy-consent-checkbox"
                      type="checkbox"
                      checked={consent}
                      onChange={(e) => setConsent(e.target.checked)}
                      className="mt-0.5 h-4 w-4 shrink-0 accent-talanta-green"
                    />
                    <span className="text-xs text-talanta-charcoal/70 leading-relaxed">
                      I agree to the{" "}
                      <Link to="/privacy" target="_blank" className="font-semibold text-talanta-green underline underline-offset-2">Privacy Policy</Link>{" "}
                      and{" "}
                      <Link to="/terms" target="_blank" className="font-semibold text-talanta-green underline underline-offset-2">Terms of Use</Link>.
                      My data is processed under the Kenya Data Protection Act, 2019.
                    </span>
                  </label>
                </>
              )}
              <button
                data-testid="send-otp-btn"
                onClick={send}
                disabled={busy || (!isSignin && !consent)}
                className="w-full rounded-xl bg-talanta-red hover:bg-talanta-red/90 py-3.5 text-white font-semibold flex items-center justify-center gap-2 transition-all hover:scale-[1.01] disabled:opacity-60"
              >
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <>{copy.cta} <ArrowRight className="h-4 w-4" /></>}
              </button>
              <p className="text-center text-xs text-talanta-charcoal/40">
                {isSignin ? (
                  <>New here?{" "}<button data-testid="auth-switch-create" onClick={() => setMode("create")} className="font-semibold text-talanta-terracotta">Create your One Talanta ID</button></>
                ) : (
                  <>Already have an ID?{" "}<button data-testid="auth-switch-signin" onClick={() => setMode("signin")} className="font-semibold text-talanta-terracotta">Sign in</button></>
                )}
              </p>
            </div>
          ) : (
            <div className="space-y-5">
              <div className="flex justify-center" data-testid="otp-input">
                <InputOTP maxLength={6} value={code} onChange={setCode} autoFocus>
                  <InputOTPGroup>
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <InputOTPSlot key={i} index={i} className="h-12 w-11 text-lg" />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>
              <button
                data-testid="verify-otp-btn"
                onClick={verify}
                disabled={busy}
                className="w-full rounded-xl bg-talanta-red hover:bg-talanta-red/90 py-3.5 text-white font-semibold flex items-center justify-center gap-2 transition-all hover:scale-[1.01] disabled:opacity-60"
              >
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <>{isSignin ? "Sign in" : "Verify & continue"} <ShieldCheck className="h-4 w-4" /></>}
              </button>
              <div className="flex items-center justify-between text-sm">
                <button onClick={() => setStep("phone")} className="text-talanta-charcoal/50 hover:text-talanta-green">
                  Change number
                </button>
                <button
                  data-testid="resend-otp-btn"
                  disabled={countdown > 0}
                  onClick={send}
                  className="inline-flex items-center gap-1.5 font-semibold text-talanta-terracotta disabled:text-talanta-charcoal/30"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  {countdown > 0 ? `Resend in ${countdown}s` : "Resend code"}
                </button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
